/**
 * Sizing strategy for TextArea and rich-text editors (ADO #69014 / #69015).
 *
 * - Default to `small` (4 visible lines) so empty fields stay compact.
 * - Use `large` (8 visible lines) only for long-form surfaces (news, campaigns).
 * - Always auto-grow with the content up to `maxLines`, then scroll — never clip.
 * - In constrained hosts (panel discussions), also cap to the host height so toolbar/Save stay visible.
 */
export type TextFieldSize = 'small' | 'large';

export const DEFAULT_TEXT_FIELD_SIZE: TextFieldSize = 'small';

export const TEXT_FIELD_SIZE = {
    small: { minLines: 4, maxLines: 16 },
    large: { minLines: 8, maxLines: 24 }
} as const;

export const TEXT_AREA_LINE_HEIGHT_REM = 1.1;
export const TEXT_AREA_PADDING_Y_REM = 2;
export const RICH_TEXT_LINE_HEIGHT_REM = 1;
export const DEFAULT_REM_PX = 16;

export const resolveTextFieldSize = (size?: TextFieldSize): TextFieldSize =>
    size === 'large' ? 'large' : DEFAULT_TEXT_FIELD_SIZE;

export const getTextAreaMinHeightCss = (size?: TextFieldSize): string => {
    const { minLines } = TEXT_FIELD_SIZE[resolveTextFieldSize(size)];
    return `calc(${minLines} * ${TEXT_AREA_LINE_HEIGHT_REM}rem + ${TEXT_AREA_PADDING_Y_REM}rem)`;
};

export const getTextAreaMaxHeightCss = (size?: TextFieldSize): string => {
    const { maxLines } = TEXT_FIELD_SIZE[resolveTextFieldSize(size)];
    return `calc(${maxLines} * ${TEXT_AREA_LINE_HEIGHT_REM}rem + ${TEXT_AREA_PADDING_Y_REM}rem)`;
};

export const getTextAreaMinHeightPx = (size?: TextFieldSize, remPx: number = DEFAULT_REM_PX): number => {
    const { minLines } = TEXT_FIELD_SIZE[resolveTextFieldSize(size)];
    return minLines * TEXT_AREA_LINE_HEIGHT_REM * remPx + TEXT_AREA_PADDING_Y_REM * remPx;
};

export const getTextAreaMaxHeightPx = (size?: TextFieldSize, remPx: number = DEFAULT_REM_PX): number => {
    const { maxLines } = TEXT_FIELD_SIZE[resolveTextFieldSize(size)];
    return maxLines * TEXT_AREA_LINE_HEIGHT_REM * remPx + TEXT_AREA_PADDING_Y_REM * remPx;
};

export const getRichTextContentMinHeightCss = (size?: TextFieldSize): string => {
    const { minLines } = TEXT_FIELD_SIZE[resolveTextFieldSize(size)];
    return `calc(${minLines} * ${RICH_TEXT_LINE_HEIGHT_REM}rem)`;
};

export const getRichTextContentMaxHeightCss = (size?: TextFieldSize): string => {
    const { maxLines } = TEXT_FIELD_SIZE[resolveTextFieldSize(size)];
    return `calc(${maxLines} * ${RICH_TEXT_LINE_HEIGHT_REM}rem)`;
};

export const getRichTextContentMinHeightPx = (size?: TextFieldSize, remPx: number = DEFAULT_REM_PX): number => {
    const { minLines } = TEXT_FIELD_SIZE[resolveTextFieldSize(size)];
    return minLines * RICH_TEXT_LINE_HEIGHT_REM * remPx;
};

export const getRichTextContentMaxHeightPx = (size?: TextFieldSize, remPx: number = DEFAULT_REM_PX): number => {
    const { maxLines } = TEXT_FIELD_SIZE[resolveTextFieldSize(size)];
    return maxLines * RICH_TEXT_LINE_HEIGHT_REM * remPx;
};

export const nextAutoGrowHeight = (scrollHeight: number, minHeight: number, maxHeight: number): number =>
    Math.min(maxHeight, Math.max(minHeight, scrollHeight));

/**
 * Caps the strategy max height so toolbar + Save stay visible in a constrained host
 * (e.g. panel discussion composer). When the host has not been measured yet, keep the strategy max.
 */
export const clampRichTextHostMaxHeight = (
    strategyMaxHeight: number,
    minHeight: number,
    hostAvailableHeight?: number
): number => {
    if (hostAvailableHeight == null || Number.isNaN(hostAvailableHeight)) {
        return strategyMaxHeight;
    }
    return Math.min(strategyMaxHeight, Math.max(minHeight, Math.floor(hostAvailableHeight)));
};
